import SummaryStrip from '../components/SummaryStrip';
import { PageHeader, DataTable, EmptyState, StatCard } from '../components/AdminPageUI';
// NotificationsPage (refactored internals, UI identical)
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Container,
  Avatar,
  Stack,
  Tooltip,
  Chip,
  Collapse,
  Badge,
  useMediaQuery,
  alpha,
  useTheme
} from "@mui/material";
import {
  Notifications,
  MarkEmailRead,
  NotificationsActive,
  ExpandMore,
  ExpandLess,
  Refresh,
  Delete,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../utils/Api";
import socket from "../utils/socket";
import { useLocation } from "react-router-dom";

/**
 * Refactor notes (internals only):
 *  - notificationsMapRef: Map(id -> notification) for O(1) updates
 *  - notificationsOrderRef: array of ids to preserve server order
 *  - local session cache with TTL (in seconds)
 *  - optimistic updates on mark/delete
 *  - memoized lists for unread/read rendering
 *  - stable socket handler and cleanup
 */

/* ----------------------- Config ----------------------- */
const CACHE_KEY = "notifications_cache_v1";
const CACHE_TTL_SEC = 60 * 2; // 2 minutes
const SOCKET_DEBOUNCE_MS = 150; // small debounce to batch socket pushes

export default function NotificationsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  // refs for efficient mutable state
  const notificationsMapRef = useRef(new Map()); // id -> notification
  const notificationsOrderRef = useRef([]); // array of ids (most recent first)
  const notificationRefs = useRef({});
  const socketBufferRef = useRef([]);
  const socketTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  // UI state (controls)
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ unread: true, read: true });
  const [versionToggle, setVersionToggle] = useState(0); // bump to re-render lists when map mutates

  // minimal user parsing
  const rawUser = localStorage.getItem("user");
  let userData;
  try { userData = rawUser ? JSON.parse(rawUser) : null; } catch (e) { userData = null; }
  const userId = userData?._id || userData?.id;

  /* --------------------- Helpers --------------------- */
  const applyMapChanges = useCallback(() => {
    // bump to a new render; this is intentionally minimal and fast
    setVersionToggle((v) => v + 1);
  }, []);

  const setNotificationInMap = useCallback((n) => {
    if (!n || !n._id) return;
    const id = String(n._id);
    const exists = notificationsMapRef.current.has(id);
    notificationsMapRef.current.set(id, n);
    if (!exists) {
      // new first in order
      notificationsOrderRef.current = [id, ...notificationsOrderRef.current];
    }
    applyMapChanges();
  }, [applyMapChanges]);

  const removeNotificationFromMap = useCallback((id) => {
    id = String(id);
    notificationsMapRef.current.delete(id);
    notificationsOrderRef.current = notificationsOrderRef.current.filter(i => i !== id);
    applyMapChanges();
  }, [applyMapChanges]);

  const getOrderedNotifications = useCallback(() => {
    // produce an array of notifications in the preserved order
    return notificationsOrderRef.current.map(id => notificationsMapRef.current.get(id)).filter(Boolean);
  }, []);

  const normalizeServerNotification = useCallback((n) => {
    // keep shape consistent with existing UI expectations
    return {
      _id: String(n._id || n.id),
      title: (n.title || "Notification").replace(/Recovered/gi, "Arrears"),
      message: (n.message || "").replace(/recovered/gi, "arrears"),
      type: n.type || "general",
      sender: n.sender || n.from || { name: "System", role: "system" },
      createdAt: n.createdAt || n.date || new Date().toISOString(),
      readBy: Array.isArray(n.readBy) ? n.readBy : [],
      isRead: !!n.isRead || (Array.isArray(n.readBy) && n.readBy.map(String).includes(String(userId))),
      ...n
    };
  }, [userId]);

  /* --------------------- Caching --------------------- */
  const readCache = useCallback(() => {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.ts || (Date.now() - parsed.ts) / 1000 > CACHE_TTL_SEC) {
        sessionStorage.removeItem(CACHE_KEY);
        return null;
      }
      return parsed.data;
    } catch (e) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
  }, []);

  const writeCache = useCallback((arr) => {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: arr }));
    } catch (e) {
      // ignore cache errors
    }
  }, []);

  /* --------------------- Fetch Notifications --------------------- */
  const fetchNotifications = useCallback(async (opts = { bypassCache: false }) => {
    setLoading(true);

    // cancel prior fetch if any
    if (abortControllerRef.current) {
      try { abortControllerRef.current.abort(); } catch (e) {}
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      if (!opts.bypassCache) {
        const cached = readCache();
        if (cached && Array.isArray(cached) && cached.length > 0) {
          // hydrate map from cache quickly
          notificationsMapRef.current.clear();
          notificationsOrderRef.current = [];
          cached.forEach(item => {
            const norm = normalizeServerNotification(item);
            notificationsMapRef.current.set(String(norm._id), norm);
            notificationsOrderRef.current.push(String(norm._id));
          });
          applyMapChanges();
          // still let it refetch in background to refresh
          // but do not block UI
        }
      }

      const res = await api.get("/notifications", { signal });
      const data = Array.isArray(res.data) ? res.data : (res.data || []);
      // hydrate map and preserve order (server provided order assumed newest first)
      notificationsMapRef.current.clear();
      notificationsOrderRef.current = [];
      data.forEach(item => {
        const norm = normalizeServerNotification(item);
        notificationsMapRef.current.set(String(norm._id), norm);
        notificationsOrderRef.current.push(String(norm._id));
      });
      applyMapChanges();
      writeCache(data);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.name === "AbortError") {
        // request was aborted — ignore
      } else {
        console.error("Failed to fetch notifications:", err);
        toast.error("Failed to load notifications");
      }
    } finally {
      setLoading(false);
    }
  }, [normalizeServerNotification, readCache, writeCache, applyMapChanges]);

  /* --------------------- Memoized Lists --------------------- */
  const orderedNotifications = useMemo(() => getOrderedNotifications(), [versionToggle, getOrderedNotifications]);
  const unread = useMemo(() => orderedNotifications.filter(n => !n.isRead), [orderedNotifications]);
  const read = useMemo(() => orderedNotifications.filter(n => n.isRead), [orderedNotifications]);

  /* --------------------- Optimistic Handlers --------------------- */
  const markAsRead = useCallback(async (id) => {
    if (!id) return;
    const key = String(id);
    const existing = notificationsMapRef.current.get(key);
    if (!existing) return;

    // optimistic update
    const prev = { ...existing };
    const updated = { ...existing, isRead: true };
    if (Array.isArray(updated.readBy) && !updated.readBy.map(String).includes(String(userId))) {
      updated.readBy = [...(updated.readBy || []).map(String), String(userId)];
    } else if (!Array.isArray(updated.readBy)) {
      updated.readBy = [String(userId)];
    }

    notificationsMapRef.current.set(key, updated);
    applyMapChanges();

    try {
      await api.patch(`/notifications/${key}/read`);
      toast.success("Notification marked as read");
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    } catch (err) {
      // revert
      notificationsMapRef.current.set(key, prev);
      applyMapChanges();
      console.error("markAsRead error:", err);
      toast.error("Failed to mark as read");
    }
  }, [applyMapChanges, userId]);

  const deleteNotification = useCallback(async (id) => {
    if (!id) return;
    const key = String(id);
    const prevExists = notificationsMapRef.current.has(key);
    const prevValue = prevExists ? notificationsMapRef.current.get(key) : null;
    if (!prevExists) return;

    // optimistic remove
    removeNotificationFromMap(key);

    try {
      await api.delete(`/notifications/${key}`);
      toast.success("Notification deleted");
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    } catch (err) {
      // revert
      if (prevValue) {
        notificationsMapRef.current.set(key, prevValue);
        notificationsOrderRef.current = [key, ...notificationsOrderRef.current.filter(i => i !== key)];
        applyMapChanges();
      }
      console.error("deleteNotification error:", err);
      toast.error("Failed to delete");
    }
  }, [removeNotificationFromMap, applyMapChanges]);

  const markAllAsRead = useCallback(async () => {
    // optimistic: mark all visible unread as read locally
    const unreadIds = orderedNotifications.filter(n => !n.isRead).map(n => String(n._id));
    if (unreadIds.length === 0) return;

    // local update
    unreadIds.forEach(id => {
      const prev = notificationsMapRef.current.get(id) || {};
      const updated = { ...prev, isRead: true, readBy: [...(prev.readBy || []).map(String), String(userId)] };
      notificationsMapRef.current.set(id, updated);
    });
    applyMapChanges();

    try {
      await api.patch('/notifications/mark-all-read');
      toast.success("All notifications marked as read");
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    } catch (err) {
      // revert by refetching (safe fallback)
      console.error("markAllAsRead error:", err);
      toast.error("Failed to mark all as read; refreshing");
      fetchNotifications({ bypassCache: true });
    }
  }, [applyMapChanges, fetchNotifications, orderedNotifications, userId]);

  const markTypesAsRead = useCallback(async (types = [], classId = null) => {
    if (!Array.isArray(types) || types.length === 0) return;
    // optimistic local mark for matching types
    const affected = orderedNotifications.filter(n => types.includes(n.type));
    affected.forEach(n => {
      const id = String(n._id);
      const prev = notificationsMapRef.current.get(id) || {};
      const updated = { ...prev, isRead: true, readBy: [...(prev.readBy || []).map(String), String(userId)] };
      notificationsMapRef.current.set(id, updated);
    });
    applyMapChanges();

    try {
      const payload = { types, class: classId };
      await api.post('/notifications/mark-read', payload);
      toast.success("Requested notifications marked as read");
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    } catch (err) {
      console.error("markTypesAsRead error:", err);
      toast.error("Failed to mark types as read; refreshing");
      fetchNotifications({ bypassCache: true });
    }
  }, [applyMapChanges, orderedNotifications, fetchNotifications, userId]);

  /* --------------------- Socket Handling --------------------- */
  const handleSocketMessage = useCallback((data) => {
    // buffer messages briefly to avoid frequent setState
    socketBufferRef.current.push(data);
    if (socketTimerRef.current) return;
    socketTimerRef.current = setTimeout(() => {
      const buf = socketBufferRef.current.splice(0);
      socketTimerRef.current = null;
      // insert all buffered notifications (newest first)
      for (let i = buf.length - 1; i >= 0; i--) {
        const raw = buf[i];
        const norm = normalizeServerNotification(raw);
        setNotificationInMap(norm);
      }
      // small toast for the last arrival
      if (buf.length > 0) {
        const last = normalizeServerNotification(buf[buf.length - 1]);
        toast.info(`🔔 ${last.title}`);
      }
    }, SOCKET_DEBOUNCE_MS);
  }, [normalizeServerNotification, setNotificationInMap]);

  /* --------------------- Effects --------------------- */
  useEffect(() => {
    fetchNotifications();

    // register socket once
    if (userId) {
      try {
        socket.emit("register", userId);
      } catch (e) {
        // ignore
      }
    }

    socket.on("notification", handleSocketMessage);

    // scroll to query param if present (after a short delay to ensure refs populated)
    const qs = new URLSearchParams(location.search);
    const scrollToId = qs.get("scrollTo");
    if (scrollToId) {
      setTimeout(() => {
        const el = notificationRefs.current[scrollToId];
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 350);
    }

    return () => {
      socket.off("notification", handleSocketMessage);
      if (socketTimerRef.current) {
        clearTimeout(socketTimerRef.current);
        socketTimerRef.current = null;
      }
      if (abortControllerRef.current) {
        try { abortControllerRef.current.abort(); } catch (e) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once

  /* --------------------- Subcomponents (memoized) --------------------- */
  const MobileNotificationCard = React.memo(function MobileNotificationCard({ notification, isUnread = false }) {
    const n = notification;
    return (
      <Card
        sx={{
          mb: 2,
          borderLeft: isUnread ? `4px solid ${theme.palette.warning.main}` : 'none',
          bgcolor: isUnread ? alpha(theme.palette.warning.main, 0.02) : 'background.paper'
        }}
        ref={(el) => (notificationRefs.current[n._id] = el)}
      >
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Typography variant="subtitle1" fontWeight="600" sx={{ flex: 1 }}>
                {n.title}
              </Typography>
              <Tooltip title={(n.createdAt ? new Date(n.createdAt).toLocaleString() : '')}>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {formatDate(n.createdAt)}
                </Typography>
              </Tooltip>
            </Stack>

            <Typography variant="body2" color="text.secondary">{n.message}</Typography>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar sx={{ width: 32, height: 32 }}>
                  {getInitials(n.sender?.name)}
                </Avatar>
                <Box>
                  <Typography variant="caption" fontWeight="500">{n.sender?.name || "System"}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{n.sender?.role}</Typography>
                </Box>
              </Stack>

              {isUnread ? (
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Mark as read">
                    <IconButton size="small" onClick={() => markAsRead(n._id)} color="primary">
                      <MarkEmailRead fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => deleteNotification(n._id)} color="error">
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ) : (
                <Chip label="Read" size="small" color="success" />
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  });

  const DesktopTableRow = React.memo(function DesktopTableRow({ n, isUnreadRow = false }) {
    return (
      <TableRow
        key={n._id}
        ref={(el) => (notificationRefs.current[n._id] = el)}
        sx={isUnreadRow ? { bgcolor: alpha(theme.palette.warning.main, 0.02) } : undefined}
      >
        <TableCell>
          <Typography fontWeight="600">{n.title}</Typography>
          <Typography variant="body2">{n.message}</Typography>
        </TableCell>

        <TableCell>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Avatar sx={{ width: 32, height: 32 }}>{getInitials(n.sender?.name)}</Avatar>
            <Box>
              <Typography variant="body2">{n.sender?.name || "System"}</Typography>
              <Typography variant="caption" color="text.secondary">{n.sender?.role}</Typography>
            </Box>
          </Stack>
        </TableCell>

        <TableCell>
          <Tooltip title={getDetailedDate(n.createdAt)}>
            <Box>
              <Typography fontWeight="600" variant="body2">{formatDate(n.createdAt)}</Typography>
            </Box>
          </Tooltip>
        </TableCell>

        <TableCell align="center">
          <Stack direction="row" spacing={0.5} justifyContent="center">
            {isUnreadRow ? (
              <>
                <Tooltip title="Mark as read">
                  <IconButton onClick={() => markAsRead(n._id)} color="primary" size="small"><MarkEmailRead fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton onClick={() => deleteNotification(n._id)} color="error" size="small"><Delete fontSize="small" /></IconButton>
                </Tooltip>
              </>
            ) : (
              <Chip label="Read" color="success" size="small" />
            )}
          </Stack>
        </TableCell>
      </TableRow>
    );
  });

  /* --------------------- UI helpers (kept same as original) --------------------- */
  function getInitials(name) {
    return name ? name.split(" ").map((n) => n[0]).join("").toUpperCase() : "S";
  }
  function formatDate(d) {
    if (!d) return "—";
    const date = new Date(d);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    if (diff < 7) return `${diff} days ago`;
    return date.toLocaleDateString();
  }
  function getDetailedDate(d) {
    return d ? new Date(d).toLocaleString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }) : "—";
  }

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  /* --------------------- Render (UI unchanged) --------------------- */
  return (
    <Container
      maxWidth="xl"
      sx={{
        py: isMobile ? 2 : 4,
        px: isMobile ? 1 : 2
      }}
    >
      {/* HEADER */}
      <PageHeader title={<>Notifications</>} description={<>Stay updated with school announcements and alerts</>} actions={<><Button variant="contained" startIcon={loading ? <CircularProgress size={16} /> : <Refresh />} onClick={() => fetchNotifications({
          bypassCache: true
        })} disabled={loading} size={isMobile ? "small" : "medium"} sx={{
          minWidth: isMobile ? 'auto' : 120,
          py: isMobile ? 0.75 : 1
        }}>
                    {loading ? "Refreshing..." : "Refresh"}
                  </Button>
        {unread.length > 0 && <Button variant="outlined" startIcon={<MarkEmailRead />} size={isMobile ? "small" : "medium"} sx={{
          minWidth: isMobile ? 'auto' : 140,
          py: isMobile ? 0.75 : 1
        }} onClick={markAllAsRead}>
                      Mark All as Read
                    </Button>}</>}></PageHeader>

      {/* ACTION CARD */}
      <SummaryStrip>
        <StatCard title="Total Notifications" value={orderedNotifications.length} icon={<NotificationsActive />} loading={loading && orderedNotifications.length === 0} />
        <StatCard title="Unread" value={unread.length} icon={<Notifications />} color="warning" loading={loading && orderedNotifications.length === 0} />
        <StatCard title="Read" value={read.length} icon={<MarkEmailRead />} color="success" loading={loading && orderedNotifications.length === 0} />
      </SummaryStrip>

      {/* UNREAD LIST */}
      {unread.length > 0 && (
        <Card sx={{ mb: 4, boxShadow: isMobile ? 1 : 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: isMobile ? 2 : 3, backgroundColor: alpha(theme.palette.warning.main, 0.05), cursor: "pointer" }} onClick={() => toggleSection("unread")}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={isMobile ? 1 : 2} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.warning.main, width: isMobile ? 36 : 40, height: isMobile ? 36 : 40 }}>
                    <Badge badgeContent={unread.length} color="error" size="small"><Notifications fontSize={isMobile ? "small" : "medium"} /></Badge>
                  </Avatar>
                  <Box>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="600" color="warning.main" sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
                      Unread Notifications
                    </Typography>
                  </Box>
                </Stack>

                <IconButton size={isMobile ? "small" : "medium"}>{expandedSections.unread ? <ExpandLess /> : <ExpandMore />}</IconButton>
              </Stack>
            </Box>

            <Collapse in={expandedSections.unread}>
              {isMobile ? (
                <Box sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    {unread.map((n) => <MobileNotificationCard key={n._id} notification={n} isUnread={true} />)}
                  </Stack>
                </Box>
              ) : (
                <Box sx={{ overflow: 'auto', maxHeight: isTablet ? '60vh' : 'none' }}>
                  <DataTable><Table sx={{ minWidth: isTablet ? 600 : 'auto' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>NOTIFICATION</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>FROM</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>TIME</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>ACTIONS</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
{unread.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                      {unread.map((n) => <DesktopTableRow key={n._id} n={n} isUnreadRow={true} />)}
                    </TableBody>
                  </Table></DataTable>
                </Box>
              )}
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* READ LIST */}
      <Card sx={{ boxShadow: isMobile ? 1 : 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: isMobile ? 2 : 3, cursor: "pointer" }} onClick={() => toggleSection("read")}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={isMobile ? 1 : 2} alignItems="center">
                <Avatar sx={{ bgcolor: theme.palette.success.main, width: isMobile ? 36 : 40, height: isMobile ? 36 : 40 }}>
                  <MarkEmailRead fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="600" sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
                    Read Notifications
                  </Typography>
                </Box>
              </Stack>

              <IconButton size={isMobile ? "small" : "medium"}>{expandedSections.read ? <ExpandLess /> : <ExpandMore />}</IconButton>
            </Stack>
          </Box>

          <Collapse in={expandedSections.read}>
            {isMobile ? (
              <Box sx={{ p: 2 }}>
                <Stack spacing={1}>
                  {read.map((n) => <MobileNotificationCard key={n._id} notification={n} isUnread={false} />)}
                </Stack>
              </Box>
            ) : (
              <Box sx={{ overflow: 'auto', maxHeight: isTablet ? '60vh' : 'none' }}>
                <DataTable><Table sx={{ minWidth: isTablet ? 600 : 'auto' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>NOTIFICATION</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>FROM</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>TIME</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>STATUS</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
{read.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                    {read.map((n) => (
                      <TableRow key={n._id}>
                        <TableCell>
                          <Typography fontWeight="600">{n.title}</Typography>
                          <Typography variant="body2">{n.message}</Typography>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 32, height: 32 }}>{getInitials(n.sender?.name)}</Avatar>
                            <Box>
                              <Typography variant="body2">{n.sender?.name || "System"}</Typography>
                              <Typography variant="caption" color="text.secondary">{n.sender?.role}</Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Tooltip title={getDetailedDate(n.createdAt)}>
                            <Typography variant="body2">{formatDate(n.createdAt)}</Typography>
                          </Tooltip>
                        </TableCell>

                        <TableCell align="center">
                          <Chip label="Read" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table></DataTable>
              </Box>
            )}
          </Collapse>
        </CardContent>
      </Card>
    </Container>
  );
}
